/**
 * GLAMRDiP Instant Gels BundleBuilder — count-driven brain, kit-builder visual parity.
 * - Count-driven: user picks N designs, tier auto-selects (highest tier where design_count <= N).
 * - Modal chrome reuses kit-builder classes (gd-kb-*, gd-tier-step, gd-kb-quickview);
 *   this JS drives the same class hooks (is-active/is-reached tiers, data-selected tiles,
 *   .gd-kb__slot slots, is-quickview pane-swap) with IG data.
 * - Cart: single /cart/add.js items[] — tier variant (_kit + Designs + __bundle_id) +
 *   each design ($0 _addon) + per-tier fixed extras ($0 _addon), all one __bundle_id.
 */
(() => {
  // Resilient init (Joel 2026-07-18): loaded from layout (head defer) so it executes on
  // every render (section-embedded <script> doesn't run on ?view=/editor). Open trigger is
  // a document-level DELEGATED listener so the cross-section hero CTA can't be orphaned.
  // hydrate() is idempotent (gdInstantHydrated guard on the modal).
  let openCurrent = null;

  function initAll() { document.querySelectorAll('[data-gd-instant]').forEach(hydrate); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);

  if (!document.__gdInstantTriggerBound) {
    document.__gdInstantTriggerBound = true;
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-gd-open-bundle-builder], [data-gd-instant-open]');
      if (!trigger) return;
      const root = document.querySelector('[data-gd-instant]');
      // Modal is portaled to <body> (see hydrate) — check document-wide, not under root.
      if (!root || !document.querySelector('[data-gd-instant-modal]')) return;
      e.preventDefault();
      hydrate(root);
      if (openCurrent) openCurrent();
    });
  }

  function hydrate(root) {
    // PORTAL-SAFE: gd-overlay-portal.js moves [data-gd-instant-modal] (and everything
    // inside it) to <body>. Resolve the modal document-wide and scope every internal
    // lookup to `modal`, never the section root.
    const modal = root.querySelector('[data-gd-instant-modal]') || document.querySelector('[data-gd-instant-modal]');
    if (!modal) return;
    if (modal.dataset.gdInstantHydrated === '1') return;
    modal.dataset.gdInstantHydrated = '1';
    root.dataset.gdInstantHydrated = '1';

    const panel      = modal.querySelector('.gd-kb-modal__panel') || modal;

    // Redigerbar mikrotekst (Adib 2026-08-02) — samme dataset-mønster som kit-builder.
    // Hver værdi falder tilbage til den OPRINDELIGE streng, så en tom indstilling
    // renderer præcis som før. Skabeloner: {n} {total} {name}.
    const L = {
      atcPending:       panel.dataset.atcPending                 || 'Pick designs to continue',
      atcReady:         panel.dataset.atcReady                   || 'Add bundle to cart',
      atcRemaining:     panel.dataset.atcRemainingTemplate       || 'V\u00e6lg {n} design mere',
      atcRemainingPl:   panel.dataset.atcRemainingPluralTemplate || 'V\u00e6lg {n} designs mere',
      counter:          panel.dataset.selectionCounterTemplate   || '{n} af {total} valgt',
      savePrefix:       panel.dataset.savePrefix                 || 'Spar',
      includesPrefix:   panel.dataset.includesPrefix             || 'Includes:',
      addToKit:         panel.dataset.addToKitLabel              || 'Add to kit',
      removeFromKit:    panel.dataset.removeFromKitLabel         || 'Remove from kit',
      kitFull:          panel.dataset.kitFullLabel               || 'Kit full \u2014 remove one first',
      soldOut:          panel.dataset.soldOutLabel               || 'Sold out',
      adding:           panel.dataset.addingLabel                || 'Adding\u2026',
      checking:         panel.dataset.checkingLabel              || 'Checking\u2026',
      oosMessage:       panel.dataset.oosMessageTemplate         || '{name} just sold out \u2014 pick another.',
      oosFallbackName:  panel.dataset.oosFallbackName            || 'That design',
    };
    const closeBtns  = modal.querySelectorAll('[data-gd-instant-close]');
    const backBtn    = modal.querySelector('[data-gd-instant-back]');
    const tiers = [...modal.querySelectorAll('[data-gd-instant-tier]')]
      .map(el => ({
        el,
        variantId: parseInt(el.dataset.variantId, 10),
        count: parseInt(el.dataset.designCount, 10),
        price: parseInt(el.dataset.price, 10),
        compare: parseInt(el.dataset.compare, 10) || null,
        title: el.dataset.title,
        extras: (() => { try { return JSON.parse(el.dataset.extras || '[]'); } catch (e) { return []; } })(),
        includes: el.dataset.includes || '',
      }))
      .sort((a, b) => a.count - b.count);

    const maxSelections = tiers.length ? tiers[tiers.length - 1].count : 0;
    const minSelections = tiers.length ? tiers[0].count : 0;

    const catChips    = [...modal.querySelectorAll('[data-gd-instant-cat]')];
    const designTiles = [...modal.querySelectorAll('[data-gd-instant-design]')];

    // Multi-instance rail hooks (desktop summary + mobile footer share attributes;
    // the JS updates all instances together — the salon dual-render pattern).
    const slotsHosts = [...modal.querySelectorAll('[data-gd-instant-slots]')];
    const statusEls  = [...modal.querySelectorAll('[data-gd-instant-status]')];
    const priceEls   = [...modal.querySelectorAll('[data-gd-instant-price]')];
    const wasEls     = [...modal.querySelectorAll('[data-gd-instant-was]')];
    const savingsEls = [...modal.querySelectorAll('[data-gd-instant-savings]')];
    const includesEls= [...modal.querySelectorAll('[data-gd-instant-includes]')];
    const atcBtns    = [...modal.querySelectorAll('[data-gd-instant-atc]')];
    const atcTexts   = [...modal.querySelectorAll('[data-gd-instant-atc-text]')];
    const progressFills = [...modal.querySelectorAll('[data-gd-instant-progress-fill]')];
    const errEl      = modal.querySelector('[data-gd-instant-error]');

    // Chip-scroll arrows (parity with salon; only surface when the strip overflows).
    const chipsWrap  = modal.querySelector('[data-gd-instant-chips-wrap]');
    const chipsTrack = modal.querySelector('[data-gd-instant-chips]');
    const chipsPrev  = modal.querySelector('[data-gd-instant-chips-prev]');
    const chipsNext  = modal.querySelector('[data-gd-instant-chips-next]');

    // Detail pane (salon quick-view pane-swap).
    const gridPane    = modal.querySelector('[data-gd-instant-pane="grid"]');
    const detailPane  = modal.querySelector('[data-gd-instant-pane="detail"]');
    const scrollRegion= modal.querySelector('.gd-kb__scroll-region');
    const dTrack   = detailPane && detailPane.querySelector('[data-gd-instant-detail-track]');
    const dPrev    = detailPane && detailPane.querySelector('[data-gd-instant-detail-prev]');
    const dNext    = detailPane && detailPane.querySelector('[data-gd-instant-detail-next]');
    const dCounter = detailPane && detailPane.querySelector('[data-gd-instant-detail-counter]');
    const dName    = detailPane && detailPane.querySelector('[data-gd-instant-detail-name]');
    const dSpec    = detailPane && detailPane.querySelector('[data-gd-instant-detail-spec]');
    const dOos     = detailPane && detailPane.querySelector('[data-gd-instant-detail-oos]');
    const dCta     = detailPane && detailPane.querySelector('[data-gd-instant-detail-cta]');
    const dCtaText = detailPane && detailPane.querySelector('[data-gd-instant-detail-cta-text]');
    const dBackEls = detailPane ? detailPane.querySelectorAll('[data-gd-instant-detail-back]') : [];
    const detailState = { tile: null, idx: 0, urls: [] };
    let _dScrollTimer = null;

    const selected = []; // [{ variantId, name, designId, image }]

    const fmt = (cents) => {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
      return new Intl.NumberFormat('en-AU', {
        style: 'currency', currency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    };
    const emit = (name, detail) => {
      document.body.dispatchEvent(new CustomEvent(name, { detail }));
      if (window.dataLayer) window.dataLayer.push({ event: name, ...detail });
    };
    const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));

    // ── Modal open/close (shared scroll-lock manager). ──────────────────────
    const open = () => {
      if (!modal.hidden) return;
      modal.hidden = false;
      if (window.gdScrollLock) window.gdScrollLock.lock(modal);
      else document.body.style.overflow = 'hidden';
      emit('instant_gels_builder_open', {});
      setTimeout(() => (modal.querySelector('.gd-kb__close') || modal.querySelector('[data-gd-instant-close]'))?.focus?.({ preventScroll: true }), 50);
    };
    const close = () => {
      if (modal.hidden) return;
      // Always return to the grid pane so a reopen doesn't land in a stale detail view.
      if (detailPane && !detailPane.hidden) closeDetail();
      modal.hidden = true;
      if (window.gdScrollLock) window.gdScrollLock.unlock(modal);
      else document.body.style.overflow = '';
    };
    openCurrent = open;
    closeBtns.forEach(b => b.addEventListener('click', close));
    // State-aware back chevron (salon pattern): in quick-view → back to grid; else close modal.
    backBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      if (panel.classList.contains('is-quickview') || (detailPane && !detailPane.hidden)) closeDetail();
      else close();
    });
    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape' || modal.hidden) return;
      if (detailPane && !detailPane.hidden) { closeDetail(); return; }
      close();
    });

    // ── Category chips — .is-active pill (salon treatment). ─────────────────
    catChips.forEach(chip => {
      chip.addEventListener('click', () => {
        const cat = chip.dataset.gdInstantCat;
        catChips.forEach(c => { c.classList.remove('is-active'); c.setAttribute('aria-pressed', 'false'); });
        chip.classList.add('is-active'); chip.setAttribute('aria-pressed', 'true');
        designTiles.forEach(t => { t.hidden = cat !== '' && t.dataset.category !== cat; });
      });
    });
    // Chip-scroll arrows: reveal only when the strip overflows; nudge on click.
    function updateChipArrows() {
      if (!chipsTrack || !chipsPrev || !chipsNext) return;
      const overflow = chipsTrack.scrollWidth > chipsTrack.clientWidth + 2;
      chipsPrev.hidden = !overflow || chipsTrack.scrollLeft <= 2;
      chipsNext.hidden = !overflow || chipsTrack.scrollLeft + chipsTrack.clientWidth >= chipsTrack.scrollWidth - 2;
    }
    chipsTrack?.addEventListener('scroll', updateChipArrows, { passive: true });
    chipsPrev?.addEventListener('click', () => chipsTrack.scrollBy({ left: -160, behavior: 'smooth' }));
    chipsNext?.addEventListener('click', () => chipsTrack.scrollBy({ left: 160, behavior: 'smooth' }));

    // ── Active/target tier — SALON LOCK-STEP MODEL (Joel 2026-07-18, revenue fix). ──
    // Round UP, never down: the target is the pinned tier (slider click) or, unpinned,
    // the CEILING tier (lowest tier whose count >= selection). So 5 designs targets the
    // 6-pack ($149) — NEVER the 4-pack ($119). Auto-upgrade: selecting past the pinned
    // tier clears the pin and re-derives the ceiling. ATC is enabled ONLY when the
    // selection EXACTLY equals the target tier's count (see render + handleAtc).
    let pinnedIdx = null;
    const activeTier = () => {
      if (tiers.length === 0) return null;
      if (pinnedIdx != null) {
        if (selected.length <= tiers[pinnedIdx].count) return tiers[pinnedIdx];
        pinnedIdx = null; // overflowed the pinned target → auto-upgrade
      }
      for (const t of tiers) if (t.count >= selected.length) return t; // ceiling
      return tiers[tiers.length - 1];
    };
    // Slider node click = set target tier (salon package click). Progress + CTA then
    // track toward it; picking past it auto-upgrades (pin clears in activeTier).
    tiers.forEach((t, i) => {
      t.el.addEventListener('click', (e) => { e.preventDefault(); pinnedIdx = i; render(); });
    });

    function renderSlots(tier) {
      const slotCount = Math.max(minSelections, selected.length, tier ? tier.count : 0);
      slotsHosts.forEach(host => {
        host.innerHTML = '';
        for (let i = 0; i < slotCount; i++) {
          const sel = selected[i];
          // Salon parity: FILLED slots are <button> (clickable → reopen detail,
          // cursor + focus-ring via button.gd-kb__slot); EMPTY slots are <div>.
          const slot = document.createElement(sel ? 'button' : 'div');
          slot.className = 'gd-kb__slot';
          slot.dataset.gdInstantSlot = '';
          slot.dataset.slotIdx = String(i);
          slot.setAttribute('role', 'listitem');
          if (sel) {
            slot.type = 'button';
            slot.setAttribute('data-filled', 'true');
            slot.setAttribute('aria-label', `Preview ${sel.name}`);
            slot.title = sel.name;
            if (sel.image) {
              const img = document.createElement('img');
              img.className = 'gd-kb__slot-img';
              img.src = sel.image; img.alt = sel.name; img.loading = 'lazy';
              slot.appendChild(img);
            } else {
              const num = document.createElement('span');
              num.className = 'gd-kb__slot-num';
              num.textContent = sel.name.slice(0, 2);
              slot.appendChild(num);
            }
            const info = document.createElement('span');
            info.className = 'gd-kb__slot-info';
            info.setAttribute('aria-hidden', 'true');
            info.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
            slot.appendChild(info);
          } else {
            slot.setAttribute('aria-label', `Slot ${i + 1}: empty`);
            const num = document.createElement('span');
            num.className = 'gd-kb__slot-num';
            num.textContent = String(i + 1);
            slot.appendChild(num);
          }
          host.appendChild(slot);
        }
      });
    }

    const render = () => {
      const tier = activeTier();          // the TARGET tier (pinned or ceiling)
      const activeIdx = tier ? tiers.indexOf(tier) : -1;
      const target = tier ? tier.count : 0;
      // LOCK-STEP: ready ONLY when the selection EXACTLY equals the target tier's count.
      const ready = !!tier && selected.length === target;

      // Tier slider: is-active on the target tier, is-reached on tiers strictly below it.
      tiers.forEach((t, i) => {
        t.el.classList.toggle('is-active', i === activeIdx);
        t.el.classList.toggle('is-reached', i < activeIdx);
        t.el.setAttribute('aria-pressed', i === activeIdx ? 'true' : 'false');
      });

      renderSlots(tier);

      // Progress bar under the slots — fills selection/target (salon).
      const pctFill = target > 0 ? Math.min(100, Math.round((selected.length / target) * 100)) : 0;
      progressFills.forEach(el => { el.style.width = pctFill + '%'; });

      // Price row reflects the TARGET tier (the next tier up at intermediate counts —
      // never the lower tier's price). $X + strike + "Save $Y" (absolute, compare − price).
      const priceStr = tier ? fmt(tier.price) : '';
      priceEls.forEach(el => { el.textContent = priceStr; });
      const hasCompare = tier && tier.compare && tier.compare > tier.price;
      wasEls.forEach(el => { el.textContent = hasCompare ? fmt(tier.compare) : ''; });      // :empty hides
      savingsEls.forEach(el => { el.textContent = hasCompare ? (L.savePrefix + ' ' + fmt(tier.compare - tier.price)) : ''; });

      // Below-CTA info chip — the target tier's included extras.
      const inc = (tier && tier.includes) ? (L.includesPrefix + ' ' + tier.includes) : '';
      includesEls.forEach(el => { el.textContent = inc; el.hidden = !inc; });

      // Status — "X of N selected" (salon).
      statusEls.forEach(el => { el.textContent = tier ? L.counter.replace('{n}', selected.length).replace('{total}', target) : ''; });

      // ATC — lock-step: enabled only at an exact tier; else "Pick N more design(s)".
      const remaining = tier ? Math.max(0, target - selected.length) : 0;
      atcBtns.forEach(b => { b.disabled = !ready; });
      atcTexts.forEach(t => {
        t.textContent = ready
          ? L.atcReady
          : (remaining > 0 ? (remaining === 1 ? L.atcRemaining : L.atcRemainingPl).replace('{n}', remaining) : L.atcPending);
      });
    };

    // ── OOS pre-validation (dual-fetch; paired retail availability is authoritative). ──
    async function preValidateTile(tile) {
      const handle = tile.dataset.productHandle;
      const variantId = String(tile.dataset.variantId || '');
      if (!handle || !variantId) return true;
      const regularHandle = handle.endsWith('-bundle') ? handle.slice(0, -'-bundle'.length) : handle;
      const fetchProduct = async (h) => {
        try {
          const resp = await fetch(`/products/${h}.js?_=${Date.now()}`, { credentials: 'same-origin', headers: { 'Cache-Control': 'no-cache', Accept: 'application/json' } });
          if (!resp.ok) return null;
          return await resp.json();
        } catch (e) { return null; }
      };
      try {
        const [duplicate, regular] = await Promise.all([
          fetchProduct(handle),
          regularHandle !== handle ? fetchProduct(regularHandle) : Promise.resolve(null),
        ]);
        if (duplicate) {
          const dupVariant = (duplicate.variants || []).find((v) => String(v.id) === variantId);
          if (dupVariant && dupVariant.available === false) return false;
        }
        if (regular && regular.available === false) {
          console.info('[gd-instant-gels-builder] OOS via paired regular:', regularHandle);
          return false;
        }
        if (regular && duplicate) {
          const dupVariant = (duplicate.variants || []).find((v) => String(v.id) === variantId);
          const dupSku = dupVariant && dupVariant.sku;
          if (dupSku) {
            const matchingRegular = (regular.variants || []).find((v) => v.sku === dupSku);
            if (matchingRegular && matchingRegular.available === false) {
              console.info('[gd-instant-gels-builder] OOS via shared-SKU regular variant:', dupSku);
              return false;
            }
          }
        }
        return true;
      } catch (e) {
        console.warn('[gd-instant-gels-builder] OOS pre-check failed, deferring to cart-add:', e && e.message);
        return true;
      }
    }

    function showOosNotice(name) {
      if (!errEl) return;
      errEl.hidden = false;
      errEl.textContent = L.oosMessage.replace('{name}', name || L.oosFallbackName);
      errEl.classList.add('gd-instant__error--oos');
      if (errEl._oosTimer) clearTimeout(errEl._oosTimer);
      errEl._oosTimer = setTimeout(() => {
        errEl.hidden = true; errEl.textContent = ''; errEl.classList.remove('gd-instant__error--oos'); errEl._oosTimer = null;
      }, 4000);
    }

    // ── Selection helpers (selection flows through the detail CTA; grid tiles open detail). ──
    function addSelected(tile) {
      const variantId = parseInt(tile.dataset.variantId, 10);
      if (selected.some(d => d.variantId === variantId)) return;
      selected.push({ variantId, name: tile.dataset.name, designId: tile.dataset.designId, image: tile.dataset.image || '' });
      tile.setAttribute('data-selected', 'true');
      emit('instant_gels_design_toggle', { design_id: tile.dataset.designId, variant_id: variantId, action: 'add' });
    }
    function removeSelected(tile) {
      const variantId = parseInt(tile.dataset.variantId, 10);
      const i = selected.findIndex(d => d.variantId === variantId);
      if (i >= 0) selected.splice(i, 1);
      tile.removeAttribute('data-selected');
      emit('instant_gels_design_toggle', { design_id: tile.dataset.designId, variant_id: variantId, action: 'remove' });
    }

    // ── Detail quick-view (salon pane-swap + gd-kb-quickview chrome). ────────
    // Photos are LAZY (perf): the tile carries only the component's own image; the full
    // retail photo set is fetched from /products/<retail>.js when the detail opens (never
    // 32 all_products pairings at render — that hit Shopify's 20-cap and dropped photos).
    function buildDetailTrack(urls, label) {
      if (!dTrack) return;
      detailState.urls = urls || [];
      dTrack.innerHTML = '';
      const frag = document.createDocumentFragment();
      (urls || []).forEach((url, i) => {
        const slide = document.createElement('div');
        slide.className = 'gd-kb-quickview__slide';
        slide.dataset.gdInstantSlide = String(i);
        const img = document.createElement('img');
        img.className = 'gd-kb-quickview__slide-img';
        img.loading = 'eager'; img.decoding = 'async';
        img.alt = label ? `${label} — image ${i + 1} of ${urls.length}` : '';
        img.src = url;
        slide.appendChild(img);
        frag.appendChild(slide);
      });
      dTrack.appendChild(frag);
    }
    async function fetchRetailPhotos(tile) {
      const handle = tile.dataset.productHandle || '';
      const retail = handle.endsWith('-bundle') ? handle.slice(0, -'-bundle'.length) : handle;
      if (!retail) return [];
      try {
        const r = await fetch(`/products/${retail}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!r.ok) return [];
        const d = await r.json();
        return (d.images || []).slice(0, 8).map((u) => {
          const url = (u || '').indexOf('//') === 0 ? ('https:' + u) : u;
          return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=900';
        }).filter(Boolean);
      } catch (e) { return []; }
    }
    function scrollDetailToSlide(idx, behavior) {
      if (!dTrack) return;
      const slide = dTrack.querySelectorAll('[data-gd-instant-slide]')[idx];
      if (!slide) return;
      const pad = parseFloat(getComputedStyle(dTrack).scrollPaddingLeft || '0') || 0;
      dTrack.scrollTo({ left: Math.max(0, slide.offsetLeft - pad), behavior: behavior || 'auto' });
    }
    function updateDetailCounter() {
      if (!dCounter) return;
      const n = detailState.urls.length;
      if (n > 1) { dCounter.textContent = `${detailState.idx + 1} / ${n}`; dCounter.hidden = false; }
      else { dCounter.textContent = ''; dCounter.hidden = true; }
    }
    function updateDetailArrows() {
      const n = detailState.urls.length;
      if (dPrev) { dPrev.hidden = n <= 1 || detailState.idx <= 0; dPrev.disabled = dPrev.hidden; }
      if (dNext) { dNext.hidden = n <= 1 || detailState.idx >= n - 1; dNext.disabled = dNext.hidden; }
    }
    function markDetailOos() {
      if (dOos) dOos.hidden = false;
      if (dCta) { dCta.disabled = true; dCta.classList.remove('is-remove'); }
      if (dCtaText) dCtaText.textContent = L.soldOut;
    }
    function updateDetailCta(tile) {
      if (!dCta || !dCtaText) return;
      const oos = tile.classList.contains('gd-instant__design--oos');
      if (dOos) dOos.hidden = !oos;
      if (oos) { dCta.disabled = true; dCta.classList.remove('is-remove'); dCtaText.textContent = L.soldOut; return; }
      const variantId = parseInt(tile.dataset.variantId, 10);
      const inSel = selected.some(d => d.variantId === variantId);
      const full = selected.length >= maxSelections && !inSel;
      dCta.classList.toggle('is-remove', inSel);
      dCta.disabled = full;
      dCtaText.textContent = inSel ? L.removeFromKit : (full ? L.kitFull : L.addToKit);
    }
    function openDetail(tile) {
      if (!detailPane) { // no detail markup → direct toggle fallback
        const vId = parseInt(tile.dataset.variantId, 10);
        if (selected.some(d => d.variantId === vId)) removeSelected(tile);
        else if (selected.length < maxSelections) addSelected(tile);
        render(); return;
      }
      detailState.tile = tile; detailState.idx = 0;
      const label = tile.dataset.displayName || tile.dataset.name || '';
      // Seed instantly with the component's own image (or a previously-fetched set), then
      // upgrade to the full retail photo set once the lazy fetch resolves.
      const seed = tile._photos || (tile.dataset.image ? [tile.dataset.image] : []);
      buildDetailTrack(seed, label);
      if (dName) dName.textContent = label;
      if (dSpec) dSpec.textContent = tile.dataset.spec || '';
      updateDetailCta(tile);
      updateDetailCounter();
      updateDetailArrows();
      if (gridPane) gridPane.hidden = true;
      detailPane.hidden = false;
      panel.classList.add('is-quickview');
      if (scrollRegion) scrollRegion.scrollTop = 0;
      requestAnimationFrame(() => scrollDetailToSlide(0, 'auto'));
      detailPane.querySelector('[data-gd-instant-detail-back]')?.focus?.({ preventScroll: true });
      emit('instant_gels_detail_open', { design_id: tile.dataset.designId, image_count: detailState.urls.length });
      if (!tile._photos) {
        fetchRetailPhotos(tile).then((urls) => {
          tile._photos = urls.length ? urls : seed;
          if (detailState.tile === tile && urls.length) {
            detailState.idx = 0;
            buildDetailTrack(tile._photos, label);
            updateDetailCounter();
            updateDetailArrows();
            requestAnimationFrame(() => scrollDetailToSlide(0, 'auto'));
          }
        });
      }
    }
    function closeDetail() {
      if (!detailPane) return;
      detailPane.hidden = true;
      if (gridPane) gridPane.hidden = false;
      panel.classList.remove('is-quickview');
      detailState.tile = null;
    }

    dTrack?.addEventListener('scroll', () => {
      if (_dScrollTimer) clearTimeout(_dScrollTimer);
      _dScrollTimer = setTimeout(() => {
        if (!dTrack || detailState.urls.length === 0) return;
        const r = dTrack.getBoundingClientRect(); const center = r.left + r.width / 2;
        let best = 0, bestD = Infinity;
        dTrack.querySelectorAll('[data-gd-instant-slide]').forEach((s, i) => {
          const rr = s.getBoundingClientRect(); const c = rr.left + rr.width / 2; const d = Math.abs(c - center);
          if (d < bestD) { bestD = d; best = i; }
        });
        if (best !== detailState.idx) { detailState.idx = best; updateDetailCounter(); updateDetailArrows(); }
      }, 80);
    }, { passive: true });
    dPrev?.addEventListener('click', (e) => { e.preventDefault(); detailState.idx = Math.max(0, detailState.idx - 1); scrollDetailToSlide(detailState.idx, 'smooth'); updateDetailCounter(); updateDetailArrows(); });
    dNext?.addEventListener('click', (e) => { e.preventDefault(); detailState.idx = Math.min(detailState.urls.length - 1, detailState.idx + 1); scrollDetailToSlide(detailState.idx, 'smooth'); updateDetailCounter(); updateDetailArrows(); });

    dCta?.addEventListener('click', async (e) => {
      e.preventDefault();
      const tile = detailState.tile;
      if (!tile || dCta.disabled) return;
      const variantId = parseInt(tile.dataset.variantId, 10);
      const inSel = selected.some(d => d.variantId === variantId);
      const prevTier = activeTier();
      if (inSel) {
        removeSelected(tile);
      } else {
        if (selected.length >= maxSelections) { updateDetailCta(tile); return; }
        // Validate BEFORE committing so an OOS design surfaces the badge inside the detail.
        dCta.disabled = true; if (dCtaText) dCtaText.textContent = L.checking;
        const ok = await preValidateTile(tile);
        if (!ok) {
          tile.classList.add('gd-instant__design--oos');
          tile.setAttribute('aria-disabled', 'true');
          emit('instant_gels_design_oos_blocked', { design_id: tile.dataset.designId, variant_id: variantId });
          showOosNotice(tile.dataset.name);
          markDetailOos();
          render();
          return; // stay in the detail so the OOS badge is visible
        }
        addSelected(tile);
      }
      const nextT = activeTier();
      if (prevTier?.variantId !== nextT?.variantId) {
        emit('instant_gels_tier_changed', { from_tier: prevTier?.variantId, to_tier: nextT?.variantId, to_count: nextT?.count });
      }
      render();
      closeDetail();
    });
    dBackEls.forEach(el => el.addEventListener('click', (e) => { e.preventDefault(); closeDetail(); }));

    // Grid tile → open the detail quick-view (selection via the detail CTA, salon pattern).
    designTiles.forEach(tile => {
      tile.addEventListener('click', (e) => { e.preventDefault(); openDetail(tile); });
    });

    // Selected-slot thumbnail → reopen that design's detail (salon slot-click parity,
    // kit-builder.js line 783). Delegated on the host so it survives renderSlots()
    // rebuilds; both desktop rail + mobile footer hosts share the behaviour.
    slotsHosts.forEach(host => {
      host.addEventListener('click', (e) => {
        const slotBtn = e.target.closest('[data-gd-instant-slot][data-filled="true"]');
        if (!slotBtn) return;
        e.preventDefault();
        const idx = parseInt(slotBtn.dataset.slotIdx, 10);
        const sel = selected[idx];
        if (!sel) return;
        // Read from dataset via variantId — tile may be chip-filtered hidden; irrelevant.
        const tile = designTiles.find(t => parseInt(t.dataset.variantId, 10) === sel.variantId);
        if (tile) openDetail(tile);
      });
    });

    // ── ATC — atomic /cart/add.js: tier (_kit) + designs ($0 _addon) + extras ($0 _addon). ──
    const handleAtc = async () => {
      const tier = activeTier();
      // LOCK-STEP revenue backstop: only add when the selection EXACTLY equals a tier
      // count. Guarantees the cart can never hold an intermediate-count bundle (e.g. a
      // 5-design bundle at the 4-pack price). Belt-and-suspenders with the disabled CTA.
      if (!tier || selected.length !== tier.count) return;
      atcBtns.forEach(b => { b.disabled = true; });
      atcTexts.forEach(t => { t.textContent = L.adding; });
      if (errEl) errEl.hidden = true;
      const bundleId = uuid();
      const designsStr = selected.map(d => d.name).join(',');
      const items = [
        { id: tier.variantId, quantity: 1, properties: { Designs: designsStr, __bundle_id: bundleId, _kit: 'true' } },
        ...selected.map(d => ({ id: d.variantId, quantity: 1, properties: { __bundle_id: bundleId, _addon: 'true' } })),
        ...(tier.extras || []).filter(x => x && x.id).map(x => ({
          id: x.id, quantity: Math.max(1, parseInt(x.qty, 10) || 1), properties: { __bundle_id: bundleId, _addon: 'true' },
        })),
      ];
      emit('instant_gels_atc_click', { tier_id: tier.variantId, design_count: selected.length, price: tier.price });
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!r.ok) { const body = await r.json().catch(() => ({})); throw new Error(body.description || 'Cart add failed'); }
        emit('instant_gels_atc_success', { tier_id: tier.variantId });
        try {
          if (window._learnq && typeof window._learnq.push === 'function') {
            const tierPriceDollars = (tier.price || 0) / 100;
            window._learnq.push(['track', 'Added to Cart', {
              $value: tierPriceDollars,
              AddedItemProductName: tier.title || 'Instant Gels Bundle',
              AddedItemProductID:   tier.variantId ? String(tier.variantId) : '',
              AddedItemQuantity:    1,
              AddedItemPrice:       tierPriceDollars,
              AddedItemURL:         window.location.origin + window.location.pathname,
              AddedItemCategories:  ['Instant Gels'],
              BundleTier:           tier.title || '',
              BundleDesigns:        selected.map(d => d.name).filter(Boolean),
              BundleDesignCount:    selected.length,
            }]);
          }
        } catch (e) {}
        document.body.dispatchEvent(new CustomEvent('product-added-to-cart'));
        document.body.dispatchEvent(new CustomEvent('gd:cart-open'));
        close();
        selected.length = 0;
        designTiles.forEach(t => t.removeAttribute('data-selected'));
        render();
      } catch (e) {
        emit('instant_gels_atc_error', { message: e.message });
        if (errEl) { errEl.textContent = e.message; errEl.hidden = false; }
        atcTexts.forEach(t => { t.textContent = L.atcReady; });
        atcBtns.forEach(b => { b.disabled = false; });
      }
    };
    atcBtns.forEach(b => b.addEventListener('click', handleAtc));

    render();
    updateChipArrows();
  }
})();

/* ig-builder: kit-builder visual parity (gd-kb-* reskin) + count-driven brain (2026-07-18) */
